import { getDateFromArray } from '../date.helpers';
import {
  getAnchorButtonUrlProps,
  trackAnalytics,
  toSlug,
} from '../utils';
import {
  DATA_TRACK_CLICK_HERO_BANNER_SECTION,
  DOM_LAYOUT_HERO_BANNER,
} from '../constants';
import { filterOutNull, isNotExpired } from '../helpers';
import { getBannerTracking } from './bannerTracking.helpers';
import globalTheme from '../../ui/theme';

const getHeroSectionButtonProps = ({
  buttonText,
  descriptiveText,
  buttonUrl,
  tags,
  locale,
  variant,
  theme,
  heroImageUrl,
}) => {
  const { url, directLink } = getAnchorButtonUrlProps(
    buttonUrl,
    locale,
  );

  return {
    buttonText,
    variant,
    size: 'medium',
    accessibleLabel: descriptiveText,
    ...trackAnalytics(
      DOM_LAYOUT_HERO_BANNER,
      `${toSlug(buttonText)}-${DOM_LAYOUT_HERO_BANNER}`,
      url,
      tags,
    ),
    ...(directLink ? { target: '_blank' } : {}),
    bannerTracking: getBannerTracking({
      bannerImgUrl: heroImageUrl,
      bannerLocation: DATA_TRACK_CLICK_HERO_BANNER_SECTION,
      linkName: toSlug(buttonText),
    }),
    theme,
  };
};

const getLabelText = ({
  startDate,
  endDate,
  locale,
}) => {
  const startDateText = getDateFromArray(
    [startDate],
    'MMMM D',
    undefined,
    locale
  );
  const endDateText = getDateFromArray(
    [endDate],
    'MMMM D',
    undefined,
    locale
  );

  return `${startDateText} - ${endDateText}`;
};

// Deprecated. LifestyleBanner shouldn't be used for HeroBanner
export const getHeroSectionBannerLifestyleBannerProps = (bannerLayout, locale) => {
  const {
    startDate,
    endDate,
    buttonUrl,
    buttonText,
    buttonDescriptiveText,
    secondaryButtonText,
    secondaryButtonUrl,
    secondaryButtonDescriptiveText,
    heroImageUrl,
    tags,
  } = bannerLayout;

  const theme = bannerLayout.buttonsDisplayTheme || 'dark';

  const primaryButton = buttonText && buttonUrl
    ? getHeroSectionButtonProps({
      locale,
      buttonText,
      descriptiveText: buttonDescriptiveText,
      buttonUrl,
      tags,
      theme,
      variant: 'primary',
      heroImageUrl,
    })
    : null;
  const secondaryButton = secondaryButtonText && secondaryButtonUrl
    ? getHeroSectionButtonProps({
      locale,
      buttonText: secondaryButtonText,
      descriptiveText: secondaryButtonDescriptiveText,
      buttonUrl: secondaryButtonUrl,
      tags,
      theme,
      variant: 'secondary',
      heroImageUrl,
    })
    : null;

  return {
    buttons: filterOutNull([
      primaryButton,
      secondaryButton,
    ]),
    labelText: getLabelText({
      startDate,
      endDate,
      locale,
    }),
  };
};

export const getHeroBannerProps = (banner, locale, trackingContext = {}) => {
  const {
    id,
    slug,
    heading,
    subheading,
    buttons,
    heroImageUrl,
    portraitTabletHeroImageUrl,
    heroImageAltText,
    startDate,
    endDate,
    textColor,
    backgroundColor,
    showDatesLabel,
    shouldReverseLayout,
    tags,
  } = banner;

  if (startDate && endDate && !isNotExpired(banner, 'startDate', 'endDate')) {
    return null;
  }

  const buttonsWithAnalytics = (buttons || []).map(({
    href,
    label,
    descriptiveText,
    openInNewTab,
    ...rest
  }) => {
    const { url } = getAnchorButtonUrlProps(
      href,
      locale,
    );

    return {
      buttonText: label,
      accessibleLabel: descriptiveText,
      ...trackAnalytics(
        `${DOM_LAYOUT_HERO_BANNER}-${slug}`,
        `${toSlug(label)}-${DOM_LAYOUT_HERO_BANNER}`,
        url,
        tags,
      ),
      ...(openInNewTab ? { target: '_blank' } : {}),
      ...rest,
      bannerTracking: getBannerTracking({
        bannerImgUrl: heroImageUrl,
        isCarousel: trackingContext.isCarousel,
        position: trackingContext.position,
        bannerLocation: DATA_TRACK_CLICK_HERO_BANNER_SECTION,
        linkName: toSlug(label),
      }),
    };
  });

  return {
    id,
    buttons: buttonsWithAnalytics,
    heroImageUrl,
    portraitTabletHeroImageUrl,
    heroImageAltText,
    heading,
    subheading,
    labelText: showDatesLabel
      ? getLabelText({
        startDate,
        endDate,
        locale,
      })
      : null,
    backgroundColor: backgroundColor || globalTheme.c.bg['feature-1'],
    shouldReverseLayout,
    textColor,
  };
};

export const getHeroBannersProps = (items, locale) => {
  const bannerItems = Array.isArray(items) ? items : [];
  const bannersToRender = filterOutNull(bannerItems.map((item) => {
    if (!item || item.__typename !== 'heroBanner') {
      return null;
    }

    if (item.startDate && item.endDate && !isNotExpired(item, 'startDate', 'endDate')) {
      return null;
    }

    return item;
  }));
  const isCarousel = bannersToRender.length > 1;

  return bannersToRender.map((banner, index) => getHeroBannerProps(
    banner,
    locale,
    {
      isCarousel,
      position: index + 1,
    },
  ));
};
